// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useOperatorConfigurations from '@hooks/queries/collateralv2/configuration/useOperatorConfigurations';
import { useState } from 'react';
import { GridColDef, GridToolbarContainer } from '@mui/x-data-grid';
import { OperatorConfiguration } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Configuration/Operator';
import { SyncButton } from '../../../components/Button/SyncButton';
import Loading from '../../../components/Loading';
import { DataTable } from '../../../components/Table/DataTable';
import RequiredCredentials from '../../../modules/Credential/RequiredCredentials';
import TemplateContract from '../../../utils/TemplateContract';
import { prettyParty } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';
import { CreateOperatorConfigurationDialog } from './CreateOperatorConfigurationDialog';

type Row = TemplateContract<OperatorConfiguration>;

const createHeaders: () => GridColDef<Row>[] = () => {
  return [
    {
      field: 'operator',
      headerName: 'Operator',
      valueGetter: (params) => prettyParty(params.row.payload.operator),
      flex: 150,
      filterable: true,
    },
    {
      field: 'User Requirements',
      headerName: 'Required Credentials for Users',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.userRequirements} />
      ),
      flex: 250,
      filterable: true,
    },
  ];
};

const Toolbar = () => {
  const [createOperatorConfigurationIsOpen, setCreateOperatorConfigurationIsOpen] = useState(false);

  return (
    <GridToolbarContainer>
      {createOperatorConfigurationIsOpen && (
        <CreateOperatorConfigurationDialog
          open={createOperatorConfigurationIsOpen}
          onClose={() => setCreateOperatorConfigurationIsOpen(false)}
        />
      )}
      <SyncButton onClick={() => setCreateOperatorConfigurationIsOpen(true)}>
        Create operator configuration
      </SyncButton>
    </GridToolbarContainer>
  );
};

export const OperatorConfigurations = () => {
  const configurations = useOperatorConfigurations();
  const isLoading = configurations.isLoading || !configurations.data;
  if (isLoading) return <Loading />;

  return (
    <PageWrapper pageName="Operator Configurations">
      <DataTable
        columns={createHeaders()}
        rows={configurations?.data || []}
        rowKey={(a) => a.contractId}
        toolbar={Toolbar}
      />
    </PageWrapper>
  );
};
